const RECOVERY_STORAGE_KEY = 'pivot:stale-release-recovery';
const RECOVERY_QUERY_KEY = '__pivot_release_reload';
const RECOVERY_GUARD_MS = 2 * 60 * 1000;

interface RecoveryMarker {
  path: string;
  at: number;
}

function errorText(reason: unknown): string {
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object') {
    const candidate = reason as { message?: unknown; reason?: unknown; payload?: unknown };
    if (typeof candidate.message === 'string') return candidate.message;
    if (candidate.reason !== undefined) return errorText(candidate.reason);
    if (candidate.payload !== undefined) return errorText(candidate.payload);
  }
  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason ?? '');
  }
}

export function isStaleReleaseAssetError(reason: unknown): boolean {
  const message = errorText(reason).toLowerCase();
  return [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'importing a module script failed',
    'failed to load module script',
    'expected a javascript-or-wasm module script',
    'mime type of "text/html"',
    'mime type “text/html”',
    'dynamically imported module'
  ].some((fragment) => message.includes(fragment));
}

function canonicalPath(url: URL): string {
  const copy = new URL(url.toString());
  copy.searchParams.delete(RECOVERY_QUERY_KEY);
  return `${copy.pathname}${copy.search}${copy.hash}`;
}

function readMarker(): RecoveryMarker | null {
  try {
    const raw = window.sessionStorage.getItem(RECOVERY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RecoveryMarker>;
    if (typeof parsed.path !== 'string' || typeof parsed.at !== 'number') return null;
    return { path: parsed.path, at: parsed.at };
  } catch {
    return null;
  }
}

export function recoverFromStaleRelease(reason: unknown, target?: string): boolean {
  if (!isStaleReleaseAssetError(reason) || typeof window === 'undefined') return false;

  const destination = new URL(target || window.location.href, window.location.origin);
  const path = canonicalPath(destination);
  const previous = readMarker();
  const now = Date.now();

  if (previous && previous.path === path && now - previous.at < RECOVERY_GUARD_MS) {
    console.error('Pivot could not recover a stale frontend asset automatically.', reason);
    return false;
  }

  try {
    window.sessionStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify({ path, at: now } satisfies RecoveryMarker));
  } catch {
    // A blocked sessionStorage must not prevent recovery.
  }

  destination.searchParams.set(RECOVERY_QUERY_KEY, String(now));
  window.location.replace(destination.toString());
  return true;
}

export function installReleaseRecovery(): () => void {
  const preloadHandler = (event: Event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    if (recoverFromStaleRelease(preloadEvent.payload ?? event)) event.preventDefault();
  };
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    if (recoverFromStaleRelease(event.reason)) event.preventDefault();
  };
  const errorHandler = (event: ErrorEvent) => {
    recoverFromStaleRelease(event.error ?? event.message);
  };

  window.addEventListener('vite:preloadError', preloadHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);
  window.addEventListener('error', errorHandler);

  return () => {
    window.removeEventListener('vite:preloadError', preloadHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    window.removeEventListener('error', errorHandler);
  };
}

export function markReleaseHealthy(): void {
  try {
    window.sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
  } catch {
    // Ignore storage restrictions.
  }

  const current = new URL(window.location.href);
  if (!current.searchParams.has(RECOVERY_QUERY_KEY)) return;
  current.searchParams.delete(RECOVERY_QUERY_KEY);
  window.history.replaceState(window.history.state, document.title, `${current.pathname}${current.search}${current.hash}`);
}
